<template>
  <AppearanceFloating
    :visible="isVisible"
    :title="$t('keyboard_appearance')"
    :auto-saved-text="$t('keyboard_appearance_auto_saved')"
    :reset-text="$t('keyboard_appearance_reset')"
    transition-name="appearance-panel-slide"
    :z-index="190"
    :is-overlay="isSelectorView"
    default-width="min(720px, calc(100vw - var(--sidebar-width-v5) - var(--sidebar-right-width-v5) - 108px))"
    default-max-height="calc(100vh - 132px)"
    overlay-width="min(920px, calc(100vw - 64px))"
    overlay-max-height="calc(100vh - 64px)"
    @close="closeKeyboardAppearancePanel"
    @reset="resetVirtualKeyboardAppearance"
  >
    <template #preview>
      <div class="keyboard_preview_stage">
        <virtual_devices v-if="selectorPreviewDevice" :device-override="selectorPreviewDevice" :key-map-override="selectorPreviewKeyMap" :key-class-resolver="resolveSelectorPreviewKeyClass" :is-disabled="true" />
      </div>
    </template>

    <setting_keyboard_appearance />
  </AppearanceFloating>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppearanceFloating from './AppearanceFloating.vue'
import virtual_devices from '@/components/device/virtual_devices.vue'
import setting_keyboard_appearance from './setting_keyboard_appearance.vue'
import { closeKeyboardAppearancePanel, keyboardAppearancePanelVisible, keyboardAppearancePreviewPid, resetVirtualKeyboardAppearance } from '@/service/keyboard_appearance'
import { DeviceManager } from '@/service/device'
import { DevicePid, type KK, type VK } from '@/define/define'
import { kbVKTexture } from '@/define/vkcode'
import { getDevice } from '@/profiles/selector'
import type { DefineDevice } from '../../../hid/define/define'
import { GetDevicesDefineKeys } from '../../../hid/define/define_fn'

// 设备选择页打开外观面板时，缺少明确预览 pid 的兜底设备，仅在选择器尚未派发任何设备时使用。
const FALLBACK_PREVIEW_PID = DevicePid.IROKMG63

const selectorPreviewConfiguredLabels = new Set(['W', 'A', 'S', 'D'])

const isVisible = computed(() => keyboardAppearancePanelVisible.value)
const isSelectorView = computed(() => DeviceManager.instance.state.currentPid === DevicePid.None)

// 优先使用 selector.vue 同步过来的当前卡片 pid；未设置则回退到 FALLBACK_PREVIEW_PID。
const selectorPreviewPid = computed(() => {
  return keyboardAppearancePreviewPid.value !== DevicePid.None ? keyboardAppearancePreviewPid.value : FALLBACK_PREVIEW_PID
})

const selectorPreviewDevice = computed(() => {
  if (!isSelectorView.value) {
    return null
  }

  return getDevice(selectorPreviewPid.value)
})

const selectorPreviewKeyMap = computed(() => {
  if (!selectorPreviewDevice.value) {
    return null
  }

  return GetDevicesDefineKeys(selectorPreviewPid.value as unknown as DefineDevice)
})

const resolveSelectorPreviewKeyClass = (_kk: KK, vk: VK) => {
  if (!isSelectorView.value || !selectorPreviewDevice.value) {
    return ''
  }

  const label = selectorPreviewDevice.value.texture[vk]?.text || kbVKTexture[vk] || ''
  return selectorPreviewConfiguredLabels.has(label.toUpperCase()) ? 'action_key_remapped' : ''
}
</script>

<style scoped lang="less">
/* 仅保留键盘预览舞台样式；通用浮动面板布局已收口在 AppearanceFloating.vue */

.keyboard_preview_stage {
  width: min(100%, 1080px);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  max-height: min(340px, 34vh);
  padding: 20px 24px;
  border-radius: var(--radius-l);
  background: var(--glass-surface-bg);
  backdrop-filter: var(--glass-surface-blur);
  -webkit-backdrop-filter: var(--glass-surface-blur);
  border: var(--panel-border-soft);
  overflow: auto;

  :deep(.board_view),
  :deep(#virtual_device),
  :deep(.device_key) {
    pointer-events: none;
  }

  :deep(.board_view) {
    width: max-content;
    min-width: 0;
    margin: 0 auto;
  }
}

@media (max-width: 960px) {
  .keyboard_preview_stage {
    min-height: 180px;
    padding: 14px;
  }
}

@media (max-height: 900px) {
  .keyboard_preview_stage {
    min-height: 160px;
    max-height: min(200px, 22vh);
    padding: 12px 16px;
  }
}

@media (max-height: 750px) {
  .keyboard_preview_stage {
    min-height: 120px;
    max-height: min(160px, 20vh);
    padding: 8px 12px;
  }
}
</style>
